<template>
  <div class="page-container news-list-page">
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">{{ $t('pages.news_list_title') }}</h1>
        <p class="breadcrumb">
          <router-link to="/">{{ $t('menu.home') }}</router-link> /
          <span>{{ $t('pages.news_list_title') }}</span>
        </p>
      </div>
    </div>

    <div class="page-content container">
      <el-row :gutter="30">
        <!-- 左侧：新闻列表 -->
        <el-col :span="18" :xs="24">
          <div class="search-wrapper">
            <el-input
              v-model="searchKeyword"
              :placeholder="$t('common.enter_search_keyword')"
              :prefix-icon="Search"
              @keyup.enter="handleSearch"
              clearable
            />
            <div class="button-row">
              <el-button type="primary" @click="handleSearch" class="search-btn">
                {{ $t('common.search') }}
              </el-button>
              <el-button
                v-if="searchKeyword.length"
                type="text"
                @click="resetSearch"
                class="reset-btn"
              >
                {{ $t('common.reset') }}
              </el-button>
            </div>
          </div>

          <div class="list-wrapper">
            <div v-if="loading" class="loading-placeholder">
              <el-skeleton :rows="6" animated />
            </div>
            <div v-else-if="newsList.length === 0" class="empty-data">
              <el-empty
                :description="searchKeyword ? $t('common.no_search_result') : $t('common.no_data')"
              >
              </el-empty>
            </div>
            <div v-else>
              <div v-for="item in newsList" :key="item.id" class="news-item">
                <div class="item-image" v-if="item.cover_image">
                  <el-image :src="item.cover_image" :alt="item.title" class="lazy-img" fit="cover">
                    <template #error>
                      <div class="image-viewer-slot image-slot item-image placeholder">
                        <div class="no-image"></div>
                      </div>
                    </template>
                  </el-image>
                </div>
                <div class="item-image placeholder" v-else>
                  <div class="no-image"></div>
                </div>
                <div class="item-content">
                  <div
                    class="news-title-link"
                    @click="handleNewsClick(item)"
                    style="cursor: pointer;"
                  >
                    <h3>{{ item.title }}</h3>
                  </div>
                  <p class="summary" v-if="item.summary">{{ item.summary }}</p>
                  <div class="meta">
                    <span class="category">{{ item.category.name }}</span>
                    <span class="time">
                    {{ $t('pages.publish_time') }}: {{ formatTime(item.publish_time) }}
                  </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!loading && totalPages > 1" class="pagination-wrapper">
            <el-pagination
              size="small"
              background
              layout="prev, pager, next"
              :current-page="currentPage"
              :page-count="totalPages"
              :disabled="loading"
              @current-change="handlePageChange"
            />
          </div>
        </el-col>

        <!-- 右侧：侧边栏（分类、热门） -->
        <el-col :span="6" :xs="24" class="sidebar-col">
          <div class="sidebar">
            <div class="sidebar-block">
              <h4>{{ $t('pages.news_list_title') }}</h4>
              <ul v-if="!categoryLoading">
                <li :class="{ active: currentCategory === null }">
                  <a href="#" @click.prevent="selectCategory(null)">{{ $t('common.all') }}</a>
                </li>
                <li
                  v-for="cat in flatCategories"
                  :key="cat.id"
                  :class="{ active: cat.id === currentCategory }"
                >
                  <a href="#" @click.prevent="selectCategory(cat.id)">{{ cat.name }}</a>
                </li>
              </ul>
              <el-skeleton :rows="5" animated v-else />
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { Search } from '@element-plus/icons-vue';
import { getNewsListApi, getNewsCategoryApi } from '@/assets/api/website';
import { useRoute, useRouter } from 'vue-router';

// -------------------------- 类型定义 --------------------------
interface CategoryItem {
  id: number;
  name: string;
  pid: number;
  level: number;
  sort: number;
  children?: CategoryItem[];
}

interface NewsCategory {
  id: number;
  name: string;
}

interface NewsListItem {
  id: number;
  title: string;
  summary?: string;
  cover_image?: string;
  publish_time: string | Date;
  category: NewsCategory;
  content_len: number;
  external_link?: string;
}

// -------------------------- 状态管理 --------------------------
const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();

// 加载状态
const loading = ref(true);
const categoryLoading = ref(true);

// 新闻列表数据
const newsList = ref<NewsListItem[]>([]);
// 分页相关
const pageSize = ref(10);
const currentPage = ref(1);
const totalPages = ref(0);
const currentCategory = ref<number | null>(null);
const searchKeyword = ref('');

const defaultImage = ref(new URL('@/assets/img/default.jpg', import.meta.url).href)
// 分类数据
const categories = ref<CategoryItem[]>([]);
const flatCategories = computed(() => {
  const flatten = (list: CategoryItem[]): CategoryItem[] => {
    return list.reduce((acc, item) => {
      acc.push(item);
      if (item.children && item.children.length) {
        acc.push(...flatten(item.children));
      }
      return acc;
    }, [] as CategoryItem[]);
  };
  return flatten(categories.value);
});

// 新增核心方法：更新路由Query参数（分类/搜索词/页码），不刷新页面，无历史记录新增
const updateRouteQuery = () => {
  // console.log(111)
  const query: any = {};
  // 有分类ID才加，无则不携带，路由更干净
  if (currentCategory.value !== null) query.category = currentCategory.value;
  // 有搜索词才加
  if (searchKeyword.value.trim()) query.keyword = searchKeyword.value.trim();
  // 页码大于1才加，第一页默认不带
  if (currentPage.value > 1) query.page = currentPage.value;
  // 替换当前路由，不会新增历史记录，避免回退时多次返回
  router.replace({ path: route.path, query });
};



// -------------------------- 核心方法 --------------------------
const formatTime = (time: string | Date) => {
  if (!time) return '';
  try {
    return format(new Date(time), 'yyyy-MM-dd');
  } catch (e) {
    return time.toString();
  }
};

const fetchCategories = async () => {
  categoryLoading.value = true;
  try {
    const res = await getNewsCategoryApi({ lang: locale.value });
    categories.value = res as CategoryItem[];
    categories.value.sort((a, b) => a.id - b.id);
  } catch (error) {
    console.error('获取新闻分类失败:', error);
    categories.value = [];
  } finally {
    categoryLoading.value = false;
  }
};

const fetchNewsList = async () => {
  loading.value = true;
  try {
    const params: any = {
      lang: locale.value,
      page: currentPage.value,
      size: pageSize.value,
    };
    if (currentCategory.value !== null) {
      params.category_id = currentCategory.value;
    }
    if (searchKeyword.value.trim()) {
      params.keyword = searchKeyword.value.trim();
    }
    const res = await getNewsListApi(params);
    newsList.value = res.list;
    totalPages.value = res.count;
    currentPage.value = res.current;
  } catch (error) {
    console.error('获取新闻列表失败:', error);
    newsList.value = [];
    totalPages.value = 0;
  } finally {
    loading.value = false;
  }
};

const handlePageChange = (page: number) => {
  currentPage.value = page;
  updateRouteQuery(); // ✅ 修改：切换页码 同步路由参数
  fetchNewsList();
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// ✅ 修改：分类切换方法 新增同步路由逻辑
const selectCategory = (categoryId: number | null) => {
  currentCategory.value = categoryId;
  currentPage.value = 1; // 切换分类默认回到第一页
  updateRouteQuery(); // 同步分类到路由参数
  fetchNewsList();
};

const handleNewsClick = (item: NewsListItem) => {
  if (item.content_len === 0 && item.external_link && item.external_link.trim() !== '') {
    window.open(item.external_link, '_blank');
  } else {
    router.push(`/news/${item.id}`);
  }
};

// ✅ 修改：搜索方法 新增同步路由逻辑
const handleSearch = () => {
  currentPage.value = 1;
  updateRouteQuery(); // 同步搜索词到路由参数
  fetchNewsList();
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// ✅ 修改：重置搜索方法 新增同步路由逻辑
const resetSearch = () => {
  searchKeyword.value = '';
  currentPage.value = 1;
  updateRouteQuery(); // 清空路由里的搜索参数
  fetchNewsList();
};

// ✅ 新增核心方法：从路由参数初始化页面状态 (优先级最高)
const initStateFromRoute = () => {
  const { query } = route;
  // 读取分类ID
  if (query.category && !isNaN(Number(query.category))) {
    currentCategory.value = Number(query.category);
  } else {
    currentCategory.value = null;
  }
  // 读取搜索关键词
  if (query.keyword && typeof query.keyword === 'string') {
    searchKeyword.value = query.keyword.trim();
  } else {
    searchKeyword.value = '';
  }
  // 读取页码
  if (query.page && !isNaN(Number(query.page))) {
    currentPage.value = Number(query.page);
  } else {
    currentPage.value = 1;
  }
};

// 统一初始化页面数据
const initPageData = async () => {
  await fetchCategories();
  initStateFromRoute(); // ✅ 修改：优先从路由初始化状态，替代原来的route.query判断
  await fetchNewsList();
}

// -------------------------- 监听与初始化 【重构完成 + 核心增强】 --------------------------
watch(
  () => locale.value,
  async () => {
    currentPage.value = 1;
    currentCategory.value = null;
    searchKeyword.value = '';
    updateRouteQuery(); // ✅ 修改：切换语言 同步清空路由参数
    await fetchCategories();
    await fetchNewsList();
  }
);

// ✅ 新增核心监听：路由参数变化 → 自动更新页面状态 + 重新请求数据 (重中之重！)
// 解决：手动改URL、刷新页面、回退页面时，路由参数和页面状态不一致的问题
watch(
  () => route.query,
  async () => {
    if (route.path === '/news/list') {
      initStateFromRoute();
      await fetchNewsList();
    }
  },
  { immediate: true, deep: true }
);

onMounted(async () => {
  await initPageData();
});

onUnmounted(() => {
});
</script>
<style scoped lang="scss">
@import '@/assets/css/common.scss';

.news-list-page {
  .page-header {
    padding: 100px 0 40px;
    background:
      linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%),
      url('@/assets/img/header.jpg') center center / cover no-repeat;
    position: relative;
    border-bottom: 1px solid rgba($primary-color, 0.1);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: inherit;
      filter: blur(2px);
      z-index: -1;
    }

    h1 {
      font-family: $font-serif;
      font-size: 2.5rem;
      font-weight: 700;
      color: $primary-color-2;
      margin-bottom: 20px;
      letter-spacing: 5px;
      text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
      position: relative;
    }

    .breadcrumb {
      font-size: 14px;
      color: #999;
      position: relative;

      a {
        color: #666;
        text-decoration: none;

        &:hover {
          color: $primary-color;
        }
      }

      span {
        color: $primary-color;
      }
    }
  }

  .page-content {
    padding: 50px;
    @media (max-width: 768px) {
      padding: 20px;
    }
  }

  .search-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    padding: 15px;
    background-color: #f9f9f9;
    border-radius: 8px;

    .el-input {
      flex: 1;
      max-width: 400px;
      border: 1px solid $primary-color;
    }
    :deep(.el-button) {
      padding: 10px 20px;
    }
    .search-btn {
      white-space: nowrap;
    }

    .reset-btn {
      color: #999;
      border: 1px solid $primary-color;
      &:hover {
        color: $primary-color;
      }
    }

    @media (max-width: 768px) {
      flex-wrap: wrap;

      .el-input {
        width: 100%;
        max-width: none;
        margin-bottom: 10px;
      }
    }
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: stretch;
      padding: 10px;

      .el-input {
        width: 100%;
        max-width: none;
        margin-bottom: 10px;
      }

      .button-row {
        display: flex;
        gap: 10px;
      }

      .search-btn, .reset-btn {
        flex: 1;
        min-width: auto;
        width: calc(50% - 12px);
      }
    }
  }

  .list-wrapper {
    min-height: 500px;
  }

  .news-item {
    display: flex;
    margin-bottom: 30px;
    padding-bottom: 30px;
    border-bottom: 1px solid #eee;
    transition: box-shadow 0.3s;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }

    .item-image {
      flex-shrink: 0;
      width: 200px;
      height: 150px;
      overflow: hidden;
      border-radius: 8px;
      margin-right: 20px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s;
      }

      &.placeholder {
        background-color: #f5f5f5;
        display: flex;
        align-items: center;
        justify-content: center;

        .no-image {
          width: 100%;
          height: 100%;
          background: url("@/assets/img/default.jpg");
          background-size: cover;
          color: #999;
          font-size: 14px;
        }
      }
    }

    &:hover .item-image img {
      transform: scale(1.05);
    }

    .item-content {
      flex-grow: 1;

      h3 {
        font-size: 20px;
        margin-top: 0;
        margin-bottom: 10px;
        color: #333;
        transition: color 0.3s;
        letter-spacing: 1px;
        &:hover {
          color: $primary-color;
        }
      }

      .summary {
        color: #666;
        line-height: 1.6;
        margin-bottom: 15px;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .meta {
        display: flex;
        gap: 20px;
        font-size: 13px;
        color: #999;

        .category {
          color: $primary-color;
          font-weight: bold;
        }
      }
    }
  }

  .pagination-wrapper {
    text-align: center;
    margin-top: 30px;
  }

  .sidebar {
    padding: 20px;
    background-color: #f9f9f9;
    border-radius: 8px;

    .sidebar-block {
      margin-bottom: 30px;

      h4 {
        font-size: 18px;
        color: $primary-color;
        border-bottom: 2px solid $primary-color;
        padding-bottom: 10px;
        margin-bottom: 15px;
      }

      ul {
        list-style: none;
        padding: 0;

        li {
          margin-bottom: 10px;
          font-size: 14px;

          a {
            color: #666;
            text-decoration: none;
            transition: color 0.3s;

            &:hover {
              color: $primary-color;
            }
          }

          &.active a {
            color: $primary-color;
            font-weight: bold;
          }
        }
      }
    }
  }

  @media (max-width: 768px) {
    .news-item {
      flex-direction: column;

      .item-image {
        width: 100%;
        height: 230px;
        margin-right: 0;
        margin-bottom: 15px;
      }
    }

    .sidebar-col {
      margin-top: 30px;
    }
  }
}
</style>